import { observer } from 'mobx-react-lite'
import { ArrowScrollTabs, Input } from '@gate/gui'
import { useTranslation } from 'next-i18next'
import { HOME_PAGE_URL, priceTabsMap } from '@/constants/index'
import { useStore } from '@/store/index'
import { eventTracker } from '@/utils/collectEvent'
import { useMemo, useState } from 'react'
import useLangPrefix from '@/hooks/useLangPrefix'
import { useAdapter } from '@/components/Adapter'
import BaseDrawer from '@/components/BaseDrawer'
import { Box, Flex } from '@mantine/core'
import BaseEmpty from '@/components/BaseEmpty'
import type { TraceItem } from 'api-price'
import { useRouter } from 'next/router'
import { stopEvent } from '@/utils/tools'
import useHistoryRouter from '@/hooks/useHistoryRouter'
import {
  OtherTabItemStyled,
  OtherTabListStyled,
  OtherTabWrapStyled,
  PriceTabsItemStyled,
  PriceTabsStyled,
} from './style'
import useCompliance from '@/hooks/useCompliance'

const PriceTabs = () => {
  const router = useRouter()
  const { t } = useTranslation()
  const { isH5 } = useAdapter()

  const { langPrefix, isZh } = useLangPrefix()
  const { baseStore, globalStore } = useStore()
  const [filterValue, setFilterValue] = useState('')

  const { commonData, isDark } = globalStore
  const { trace, priceTab } = baseStore
  const { historyRouter } = useHistoryRouter()
  const { fiatTrate } = useCompliance()

  const activeItem = useMemo<TraceItem>(() => {
    return (trace?.list as any[])?.find(item => item.id === priceTab)
  }, [priceTab, trace?.list])

  const filterTraceList = useMemo(() => {
    const list = (trace?.list as any[])?.filter(item =>
      `${item?.[router?.locale ?? ''] ?? (isZh ? (item?.zh ?? item?.en) : item?.en)}`
        ?.toLocaleUpperCase()
        .includes(filterValue?.toLocaleUpperCase())
    )

    return list
  }, [trace?.list, router?.locale, isZh, filterValue])

  const options = useMemo(() => {
    if (isH5) {
      return [
        {
          children: (
            <PriceTabsItemStyled
              onClick={e => {
                stopEvent(e)
                historyRouter({ tab: HOME_PAGE_URL.Favorites, type: 'price' })
              }}
              href={`${langPrefix}/price/view/favorites`}
            >
              <svg key="icon" className="iconpark-icon">
                <use href="#v31-20px-fill-star"></use>
              </svg>
            </PriceTabsItemStyled>
          ),
          value: HOME_PAGE_URL.Favorites,
          isShow: !commonData?.is_login,
        },
        {
          children: (
            <PriceTabsItemStyled
              onClick={e => {
                stopEvent(e)
                eventTracker('price_treade_tab_click', {
                  first_tab_name: priceTabsMap[HOME_PAGE_URL.My],
                })
                historyRouter({ tab: HOME_PAGE_URL.My, type: 'price' })
              }}
              href={`${langPrefix}/price/view/my`}
            >
              {t('P.My')}
            </PriceTabsItemStyled>
          ),
          value: HOME_PAGE_URL.My,
          isShow: !commonData?.is_login,
        },
        {
          children: (
            <PriceTabsItemStyled
              onClick={e => {
                stopEvent(e)
                eventTracker('price_treade_tab_click', {
                  first_tab_name: priceTabsMap[HOME_PAGE_URL.Market],
                })
                historyRouter({ tab: HOME_PAGE_URL.Market, type: 'price' })
              }}
              href={`${langPrefix}/price`}
            >
              {t('P.Tradable')}
            </PriceTabsItemStyled>
          ),
          value: HOME_PAGE_URL.Market,
        },
        {
          children: (
            <PriceTabsItemStyled
              onClick={e => {
                stopEvent(e)
                eventTracker('price_treade_tab_click', {
                  first_tab_name: priceTabsMap[HOME_PAGE_URL.Mcap],
                })
                historyRouter({ tab: HOME_PAGE_URL.Mcap, type: 'price' })
              }}
              href={`${langPrefix}/price/view/market-cap`}
            >
              {t('P.Mcap')}
            </PriceTabsItemStyled>
          ),
          value: HOME_PAGE_URL.Mcap,
        },
        {
          children: (
            <PriceTabsItemStyled
              onClick={e => {
                stopEvent(e)
                eventTracker('price_treade_tab_click', {
                  first_tab_name: priceTabsMap[HOME_PAGE_URL.New],
                })
                historyRouter({ tab: HOME_PAGE_URL.New, type: 'price' })
              }}
              href={`${langPrefix}/price/view/new-cryptocurrencies`}
            >
              {t('P.New')}
            </PriceTabsItemStyled>
          ),
          value: HOME_PAGE_URL.New,
        },
        {
          children: (
            <PriceTabsItemStyled
              onClick={e => {
                stopEvent(e)
                eventTracker('price_treade_tab_click', {
                  first_tab_name: priceTabsMap[HOME_PAGE_URL.Gainers],
                })
                historyRouter({ tab: HOME_PAGE_URL.Gainers, type: 'price' })
              }}
              href={`${langPrefix}/price/view/crypto-gainers`}
            >
              {t('P.Gainers')}
            </PriceTabsItemStyled>
          ),
          value: HOME_PAGE_URL.Gainers,
        },
        ...(fiatTrate
          ? [
              {
                children: (
                  <PriceTabsItemStyled
                    onClick={e => {
                      stopEvent(e)
                      eventTracker('price_treade_tab_click', {
                        first_tab_name: priceTabsMap[HOME_PAGE_URL.Losers],
                      })
                      historyRouter({ tab: HOME_PAGE_URL.Losers, type: 'price' })
                    }}
                    href={`${langPrefix}/price/view/crypto-losers`}
                  >
                    {t('P.Losers')}
                  </PriceTabsItemStyled>
                ),
                value: HOME_PAGE_URL.Losers,
              },
            ]
          : [
              {
                children: (
                  <PriceTabsItemStyled
                    onClick={e => {
                      stopEvent(e)
                      eventTracker('price_treade_tab_click', {
                        first_tab_name: priceTabsMap[HOME_PAGE_URL.Losers],
                      })
                      historyRouter({ tab: HOME_PAGE_URL.Losers, type: 'price' })
                    }}
                    href={`${langPrefix}/price/view/crypto-losers`}
                  >
                    {t('P.Losers')}
                  </PriceTabsItemStyled>
                ),
                value: HOME_PAGE_URL.Losers,
              },
              {
                children: (
                  <PriceTabsItemStyled
                    onClick={e => {
                      stopEvent(e)
                      eventTracker('price_treade_tab_click', {
                        first_tab_name: priceTabsMap[HOME_PAGE_URL.FIAT],
                      })
                      historyRouter({ tab: HOME_PAGE_URL.FIAT, type: 'price' })
                    }}
                    href={`${langPrefix}/price/view/fiat`}
                  >
                    {t('P.FIAT')}
                  </PriceTabsItemStyled>
                ),
                value: HOME_PAGE_URL.FIAT,
                // isShow: !commonData?.is_login,
              },
            ]),
      ].concat(
        (trace?.list as any[])?.map((item: any) => ({
          children: (
            <PriceTabsItemStyled
              onClick={e => {
                stopEvent(e)
                historyRouter({ tab: item?.id, type: 'price' })
              }}
              href={`${langPrefix}/price/view/${item?.id}`}
            >
              {(item?.[router?.locale ?? ''] ?? isZh) ? (item?.zh ?? item?.en) : item?.en}
            </PriceTabsItemStyled>
          ),
          value: item?.id ?? '',
        }))
      )
    }

    return [
      {
        children: (
          <PriceTabsItemStyled
            onClick={e => {
              stopEvent(e)
              historyRouter({ tab: HOME_PAGE_URL.Favorites, type: 'price' })
            }}
            href={`${langPrefix}/price/view/favorites`}
          >
            <svg key="icon" className="iconpark-icon">
              <use href="#v31-20px-fill-star"></use>
            </svg>
          </PriceTabsItemStyled>
        ),
        value: HOME_PAGE_URL.Favorites,
        isShow: !commonData?.is_login,
      },
      {
        children: (
          <PriceTabsItemStyled
            onClick={e => {
              stopEvent(e)
              eventTracker('price_treade_tab_click', {
                first_tab_name: priceTabsMap[HOME_PAGE_URL.My],
              })
              historyRouter({ tab: HOME_PAGE_URL.My, type: 'price' })
            }}
            href={`${langPrefix}/price/view/my`}
          >
            {t('P.My')}
          </PriceTabsItemStyled>
        ),
        value: HOME_PAGE_URL.My,
        isShow: !commonData?.is_login,
      },
      {
        children: (
          <PriceTabsItemStyled
            onClick={e => {
              stopEvent(e)
              eventTracker('price_treade_tab_click', {
                first_tab_name: priceTabsMap[HOME_PAGE_URL.Market],
              })
              historyRouter({ tab: HOME_PAGE_URL.Market, type: 'price' })
            }}
            href={`${langPrefix}/price`}
          >
            {t('P.Tradable')}
          </PriceTabsItemStyled>
        ),
        value: HOME_PAGE_URL.Market,
      },
      {
        children: (
          <PriceTabsItemStyled
            onClick={e => {
              stopEvent(e)
              eventTracker('price_treade_tab_click', {
                first_tab_name: priceTabsMap[HOME_PAGE_URL.Mcap],
              })
              historyRouter({ tab: HOME_PAGE_URL.Mcap, type: 'price' })
            }}
            href={`${langPrefix}${HOME_PAGE_URL.Mcap}`}
          >
            {t('P.Mcap')}
          </PriceTabsItemStyled>
        ),
        value: HOME_PAGE_URL.Mcap,
      },
      {
        children: (
          <PriceTabsItemStyled
            onClick={e => {
              stopEvent(e)
              eventTracker('price_treade_tab_click', {
                first_tab_name: priceTabsMap[HOME_PAGE_URL.New],
              })
              historyRouter({ tab: HOME_PAGE_URL.New, type: 'price' })
            }}
            href={`${langPrefix}${HOME_PAGE_URL.New}`}
          >
            {t('P.New')}
          </PriceTabsItemStyled>
        ),
        value: HOME_PAGE_URL.New,
      },
      {
        children: (
          <PriceTabsItemStyled
            onClick={e => {
              stopEvent(e)
              eventTracker('price_treade_tab_click', {
                first_tab_name: priceTabsMap[HOME_PAGE_URL.Gainers],
              })
              historyRouter({ tab: HOME_PAGE_URL.Gainers, type: 'price' })
            }}
            href={`${langPrefix}${HOME_PAGE_URL.Gainers}`}
          >
            {t('P.Gainers')}
          </PriceTabsItemStyled>
        ),
        value: HOME_PAGE_URL.Gainers,
      },
      ...(fiatTrate
        ? [
            {
              children: (
                <PriceTabsItemStyled
                  onClick={e => {
                    stopEvent(e)
                    eventTracker('price_treade_tab_click', {
                      first_tab_name: priceTabsMap[HOME_PAGE_URL.Losers],
                    })
                    historyRouter({ tab: HOME_PAGE_URL.Losers, type: 'price' })
                  }}
                  href={`${langPrefix}/price/view/crypto-losers`}
                >
                  {t('P.Losers')}
                </PriceTabsItemStyled>
              ),
              value: HOME_PAGE_URL.Losers,
            },
          ]
        : [
            {
              children: (
                <PriceTabsItemStyled
                  onClick={e => {
                    stopEvent(e)
                    eventTracker('price_treade_tab_click', {
                      first_tab_name: priceTabsMap[HOME_PAGE_URL.Losers],
                    })
                    historyRouter({ tab: HOME_PAGE_URL.Losers, type: 'price' })
                  }}
                  href={`${langPrefix}/price/view/crypto-losers`}
                >
                  {t('P.Losers')}
                </PriceTabsItemStyled>
              ),
              value: HOME_PAGE_URL.Losers,
            },
            {
              children: (
                <PriceTabsItemStyled
                  onClick={e => {
                    stopEvent(e)
                    eventTracker('price_treade_tab_click', {
                      first_tab_name: priceTabsMap[HOME_PAGE_URL.FIAT],
                    })
                    historyRouter({ tab: HOME_PAGE_URL.FIAT, type: 'price' })
                  }}
                  href={`${langPrefix}/price/view/fiat`}
                >
                  {t('P.FIAT')}
                </PriceTabsItemStyled>
              ),
              value: HOME_PAGE_URL.FIAT,
              // isShow: !commonData?.is_login,
            },
          ]),
      {
        children: (
          <BaseDrawer
            rightIconGap={2}
            isButton={false}
            isRight={true}
            title={''}
            rightIcon={
              <svg className="iconpark-icon">
                <use href="#icon-12px-jiantou-xia-fill"></use>
              </svg>
            }
            target={
              <span>
                {activeItem?.[router.locale ?? ''] ??
                  (isZh ? (activeItem?.zh ?? activeItem?.en) : activeItem?.en) ??
                  t('P.Others')}
              </span>
            }
          >
            <OtherTabWrapStyled>
              <Box px={12} py={8}>
                <Input
                  styles={{ wrapper: { width: '100%' } }}
                  icon={
                    <svg className="iconpark-icon size-14">
                      <use href="#v31-16px-line-sousuo"></use>
                    </svg>
                  }
                  placeholder={t('P.Search') + ''}
                  value={filterValue}
                  onChange={(e: any) => {
                    setFilterValue(e.target.value)
                  }}
                  onClear={() => {
                    setFilterValue('')
                  }}
                />
              </Box>

              <OtherTabListStyled>
                {filterTraceList?.length ? (
                  <>
                    {filterTraceList?.map((item: any) => (
                      <OtherTabItemStyled
                        onClick={e => {
                          stopEvent(e)
                          historyRouter({ tab: item?.id, type: 'price' })
                        }}
                        key={item?.id}
                        data-active={String(priceTab === item?.id)}
                        href={`${langPrefix}/price/view/${item?.id}`}
                      >
                        {item?.[router.locale ?? ''] ?? (isZh ? (item?.zh ?? item?.en) : item?.en)}

                        {priceTab === item?.id && (
                          <svg className="iconpark-icon size-16 text-brand-1">
                            <use href="#v31-16px-success"></use>
                          </svg>
                        )}
                      </OtherTabItemStyled>
                    ))}
                  </>
                ) : (
                  <Flex py={24}>
                    <BaseEmpty type="search" />
                  </Flex>
                )}
              </OtherTabListStyled>
            </OtherTabWrapStyled>
          </BaseDrawer>
        ),
        value: HOME_PAGE_URL.Others,
      },
    ]
  }, [
    activeItem,
    commonData?.is_login,
    filterTraceList,
    filterValue,
    isH5,
    isZh,
    langPrefix,
    historyRouter,
    priceTab,
    router.locale,
    t,
    trace?.list,
    fiatTrate,
  ])

  return (
    <PriceTabsStyled data-dark={isDark}>
      <ArrowScrollTabs
        type="primary"
        option={options?.filter(item => !item.isShow)}
        onTabChange={(tab: HOME_PAGE_URL) => historyRouter({ tab: tab, type: 'price' })}
        value={Object.values(HOME_PAGE_URL).includes(priceTab) ? priceTab : HOME_PAGE_URL.Others}
      />
    </PriceTabsStyled>
  )
}

export default observer(PriceTabs)
